<script setup lang="ts">
import { Head, Link, usePage } from "@inertiajs/vue3";
import { ref } from "vue";

import SessionListItem from "@/components/session-list-item.vue";
import { Button } from "@/components/ui/button/index";
import { useCsrfToken } from "@/composables/use-csrf-token";
import SettingsLayout from "@/layouts/settings/layout";
import { sessionPath, settingsApiKeysPath } from "@/routes";

defineOptions({ inheritAttrs: false });
interface Session {
  id: string;
  user_agent: string;
  ip_address: string;
  created_at: string;
}

interface Props {
  sessions: Session[];
}

interface ApiKeyResponse {
  token: string;
  session: Session;
}

const props = defineProps<Props>();
const page = usePage<{ auth?: { session?: { id: string } } }>();
const csrfToken = useCsrfToken();

const token = ref<string | null>(null);
const busy = ref(false);
const error = ref<string | null>(null);
const sessions = ref<Session[]>([...props.sessions]);

const generateToken = async () => {
  busy.value = true;
  error.value = null;
  token.value = null;

  try {
    // This endpoint returns JSON (not an Inertia response), so we use fetch here.
    const response = await fetch(settingsApiKeysPath(), {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": csrfToken.value,
      },
      credentials: "same-origin",
    });

    if (!response.ok) throw new Error("Unable to generate API token.");

    const payload = (await response.json()) as ApiKeyResponse;
    token.value = payload.token;
    sessions.value = [payload.session, ...sessions.value];
  } catch (err) {
    error.value =
      err instanceof Error ? err.message : "Unable to generate API token.";
  } finally {
    busy.value = false;
  }
};

const copyToken = async () => {
  if (!token.value) return;

  try {
    await navigator.clipboard.writeText(token.value);
  } catch {
    error.value = "Copy failed. Please copy manually.";
  }
};
</script>

<template>
  <Head title="API keys" />

  <main
    class="relative flex h-full min-h-0 w-full flex-col gap-3 bg-muted/50 p-3"
  >
    <SettingsLayout
      current="api_keys"
      title="API keys"
      description="Generate and revoke API bearer tokens."
    >
      <div class="rounded-xl border border-dashed p-4">
        <div class="flex flex-wrap gap-2">
          <Button type="button" :disabled="busy" @click="generateToken">
            {{ busy ? "Generating..." : "Generate token" }}
          </Button>

          <Button
            v-if="token"
            type="button"
            variant="outline"
            @click="copyToken"
          >
            Copy token
          </Button>
        </div>

        <p v-if="error" class="mt-3 text-sm text-destructive">{{ error }}</p>

        <div v-if="token" class="mt-3 rounded-md border bg-muted/40 p-3">
          <p
            class="mb-1 text-xs font-medium uppercase tracking-wide text-muted-foreground"
          >
            New token
          </p>
          <p class="break-all font-mono text-xs">{{ token }}</p>
        </div>
      </div>

      <div class="mt-6 space-y-3">
        <SessionListItem
          v-for="session in sessions"
          :key="session.id"
          :session="session"
          :current-session-id="page.props.auth?.session?.id"
          title="API token"
          date-label="Created"
        >
          <template #action>
            <Button variant="destructive" size="sm" as-child>
              <Link
                :href="sessionPath({ id: session.id })"
                method="delete"
                as="button"
              >
                Revoke
              </Link>
            </Button>
          </template>
        </SessionListItem>

        <p v-if="sessions.length === 0" class="text-sm text-muted-foreground">
          No API tokens yet.
        </p>
      </div>
    </SettingsLayout>
  </main>
</template>
